import { useEffect, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { useSupabase } from '../contexts/SupabaseContext';
import { detectDeviceInfo } from '../utils/deviceDetector';

const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes
const ADMIN_EMAIL = 'rica@presida.club';

export function useSessionTracking() {
  const { user } = useAuth();
  const { supabase } = useSupabase();
  const sessionIdRef = useRef<string | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isTracking, setIsTracking] = useState(false);

  useEffect(() => {
    if (!user) {
      if (sessionIdRef.current) {
        endSession();
      }
      return;
    }

    if (user.email === ADMIN_EMAIL) {
      return;
    }

    startSession();

    // Cleanup on unmount or user logout
    return () => {
      if (sessionIdRef.current) {
        endSession();
      }
    };
  }, [user]);

  const startSession = async () => {
    try {
      const deviceInfo = detectDeviceInfo();

      const { data, error } = await supabase.rpc('start_user_session', {
        p_device_info: deviceInfo.device,
        p_browser_info: deviceInfo.browser,
        p_os_info: deviceInfo.os,
        p_ip_address: null, // IP will be captured server-side if needed
      });

      if (error) {
        console.error('Error starting session:', error);
        return;
      }

      sessionIdRef.current = data as string;
      setIsTracking(true);

      // Start heartbeat to keep session active
      startHeartbeat();

      // Handle page unload to end session
      window.addEventListener('beforeunload', handleBeforeUnload);
    } catch (error) {
      console.error('Error starting session:', error);
    }
  };

  const endSession = async () => {
    if (!sessionIdRef.current) return;

    try {
      await supabase.rpc('end_user_session', {
        p_session_id: sessionIdRef.current,
      });

      sessionIdRef.current = null;
      setIsTracking(false);
      stopHeartbeat();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    } catch (error) {
      console.error('Error ending session:', error);
    }
  };

  const startHeartbeat = () => {
    heartbeatIntervalRef.current = setInterval(async () => {
      try {
        // Update last_active_at
        await supabase
          .from('profiles')
          .update({ last_active_at: new Date().toISOString() })
          .eq('id', user?.id);
      } catch (error) {
        console.error('Error updating heartbeat:', error);
      }
    }, HEARTBEAT_INTERVAL);
  };

  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  const handleBeforeUnload = () => {
    if (sessionIdRef.current) {
      // Use sendBeacon for reliable tracking on page unload
      const formData = new FormData();
      formData.append('session_id', sessionIdRef.current);
      navigator.sendBeacon('/api/end-session', formData);
    }
  };

  return {
    sessionId: sessionIdRef.current,
    isTracking,
  };
}
